/**
 * TotalsBlock — the house totals stack (`record.html` `.tot`; WEB TIER A · WT4).
 *
 * PRESENTATION ONLY, AND STRUCTURALLY SO. Every figure arrives as a rendered
 * node the caller built (on the quote surfaces, `ServerAmount`/`ServerDeduction`
 * over the wire's own strings — `features/quotes/money.tsx` holds that
 * boundary's argument); this file contains no `+`, no `reduce`, no `Number()`,
 * so a stack whose rows disagree with its grand total is the CALLER lying, and
 * the caller is handed nothing to derive with. `features/quotes/money.tsx`'s
 * `TotalsBlock` is the pre-Tier-A card (heading, stale treatment, D6 footer)
 * still worn by the builder and the sales-order record; THIS is the mockup's
 * bare stack that sits inside a record card's own footer. The builder/SO
 * migration onto the house grammar is Tier B work (ledger item 6), not folded.
 *
 * THE GRAND ROW LEADS WITH ITS LABEL — "Grand total" then the figure — and
 * that order is a CONTRACT, not typography: the browser smoke's
 * `renderedGrandTotal()` locates `div` text beginning `Grand total`
 * (`tests/quotes.smoke.spec.ts:213`). Pinned in `record-house.test.tsx`.
 *
 * Quiet row labels are `text/secondary` — every stack sits inside a card on
 * `bg/surface` (4.64), never on the canvas. The grand rule is the one
 * `border/strong` hairline the mockup draws (`.tot-g`): heavier than the row
 * hairlines because it closes the arithmetic.
 */

import type { ReactNode } from 'react';
import { cn } from '../../lib/cn';

export interface TotalsRowSpec {
  key: string;
  label: string;
  /** A visible second line under the label (a tax row's base). */
  detail?: string;
  /** The rendered figure — the caller's node, typically a `ServerAmount`. */
  value: ReactNode;
}

export interface TotalsBlockProps {
  rows: readonly TotalsRowSpec[];
  grand: { label: string; value: ReactNode };
  className?: string;
}

export function TotalsBlock({ rows, grand, className }: TotalsBlockProps): React.JSX.Element {
  return (
    <div data-slot="totals" className={cn('flex flex-col', className)}>
      {rows.map((row) => (
        <div key={row.key} className="flex items-baseline justify-between gap-4 py-1.5">
          <span className="flex min-w-0 flex-col">
            <span className="text-body13 text-fg-secondary">{row.label}</span>
            {row.detail === undefined ? null : (
              <span className="text-label text-fg-secondary">{row.detail}</span>
            )}
          </span>
          <span className="text-body13 text-fg tnum">{row.value}</span>
        </div>
      ))}
      <div className="mt-1 flex items-baseline justify-between gap-4 border-t border-line-strong pt-2.5">
        <span className="text-body font-medium text-fg">{grand.label}</span>
        <span data-slot="grand-total" className="text-h3 text-fg tnum">
          {grand.value}
        </span>
      </div>
    </div>
  );
}
